import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronsUpDown, Search } from 'lucide-react'
import { cn } from '@/lib/utils'

export type SearchableOption = { value: string; label: string }

// Estimated max panel height (search bar + capped list) for the flip-up check.
const PANEL_MAX_HEIGHT = 340

/**
 * The narrowest a dropdown panel may be, whatever its trigger measures.
 *
 * 240px, matching the `panelWidth={240}` two callers had already chosen by hand
 * (`AssigneeSelect`, the renderer's process picker) rather than introducing a
 * third number for the same judgement.
 */
const MIN_PANEL_WIDTH = 240

type PanelPos = { top?: number; bottom?: number; left: number; width: number }

/**
 * Dependency-free searchable single-select (combobox). Filters client-side and
 * caps the rendered list so it stays fast with thousands of options — the tail is
 * reachable by refining the search. Styled to match the shadcn Select trigger.
 *
 * The panel renders in a body portal with position:fixed and a top-of-stack
 * z-index, so overflow-hidden/auto ancestors (table wrappers, cards, dialogs)
 * can never crop it; it flips upward when the viewport has no room below.
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled,
  className,
  buttonClassName,
  trigger,
  triggerTitle,
  panelWidth,
  maxRender = 50,
  emptyMessage = 'No matches.',
  renderOption,
  onSearchChange,
  selectedOption,
}: {
  value: string
  onChange: (value: string) => void
  options: SearchableOption[]
  placeholder?: string
  disabled?: boolean
  className?: string
  /** Extra classes for the trigger button (e.g. a compact height). With a
   *  custom `trigger` these REPLACE the default select chrome entirely. */
  buttonClassName?: string
  /** Custom trigger content (e.g. an avatar). Replaces the label + chevron. */
  trigger?: React.ReactNode
  /** Hover tooltip for the trigger (mostly useful with a custom trigger). */
  triggerTitle?: string
  /** Fixed panel width in px — needed when the trigger is narrower than a
   *  usable panel (avatar triggers). Defaults to the trigger's width. */
  panelWidth?: number
  maxRender?: number
  /** Shown when there is nothing to choose from at all. A search that matches
   *  nothing still says "No matches." — an empty list and an over-narrow query
   *  are different problems and read differently to the user. */
  emptyMessage?: string
  /** Custom renderer for each dropdown option. Replaces the default truncated label. */
  renderOption?: (option: SearchableOption) => React.ReactNode
  /** When provided, delegates filtering to the caller (server-side search).
   *  The internal client-side filter is skipped and options are shown as-is. */
  onSearchChange?: (query: string) => void
  /** Label for a `value` deliberately kept OUT of `options` — e.g. a retired
   *  version a record is still pinned to. Shown on the trigger only, so the
   *  current selection stays readable without becoming newly selectable. */
  selectedOption?: SearchableOption
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [pos, setPos] = useState<PanelPos | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const selected =
    options.find((o) => o.value === value) ??
    (selectedOption?.value === value ? selectedOption : undefined)

  const filtered = useMemo(() => {
    if (onSearchChange) return options
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, query, onSearchChange])

  const shown = filtered.slice(0, maxRender)
  const hiddenCount = filtered.length - shown.length

  const updatePos = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect()
    if (!r) return
    /**
     * The panel is at least readable, however narrow the trigger is.
     *
     * It used to be exactly the trigger's width, which is fine for a full-width
     * picker and unusable in a table column: a 103px trigger opened a 103px
     * panel whose search box had 55px of room after the icon and padding, and
     * whose options were clipped. A floor costs a wide trigger nothing (`max`
     * keeps its own width) and the `left` clamp below already stops a wider
     * panel running off the viewport.
     *
     * An explicit `panelWidth` still wins, so a caller that has chosen a width
     * keeps it.
     */
    const width = panelWidth ?? Math.max(r.width, MIN_PANEL_WIDTH)
    // Clamp so a fixed-width panel never runs off the viewport edge.
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8))
    const spaceBelow = window.innerHeight - r.bottom
    const openUp = spaceBelow < PANEL_MAX_HEIGHT && r.top > spaceBelow
    setPos(
      openUp
        ? { bottom: window.innerHeight - r.top + 4, left, width }
        : { top: r.bottom + 4, left, width },
    )
  }, [panelWidth])

  // Track the trigger while open so the panel follows scrolling containers.
  useEffect(() => {
    if (!open) return
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    return () => {
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [open, updatePos])

  // Read through a ref so the effect below can stay keyed on `open` alone. A
  // caller passing an inline lambda changes this identity every render, and as
  // a dependency that would re-run the effect continuously.
  const onSearchChangeRef = useRef(onSearchChange)
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange
  })

  // Focus the search box when the panel opens; clear the search when it closes.
  //
  // Keyed on `open`, so it covers EVERY close path — pick, Escape, backdrop
  // click, trigger toggle — without each one having to remember. Delegated
  // (server-side) search has to be cleared on the caller too: clearing only the
  // local query left the caller holding its last filtered `options`, so
  // reopening showed stale results under an empty search box.
  const wasOpen = useRef(false)
  useEffect(() => {
    if (open) {
      inputRef.current?.focus()
    } else if (wasOpen.current) {
      // Guarded on a real open→closed transition, so a mounted-but-never-opened
      // select does not fire a search reset at the caller.
      setQuery('')
      onSearchChangeRef.current?.('')
    }
    wasOpen.current = open
  }, [open])

  function pick(v: string) {
    onChange(v)
    setOpen(false)
  }

  return (
    <div className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        title={triggerTitle}
        onClick={() => {
          updatePos()
          setOpen((v) => !v)
        }}
        className={
          trigger !== undefined
            ? buttonClassName
            : cn(
                'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm',
                'focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                buttonClassName,
              )
        }
      >
        {trigger !== undefined ? (
          trigger
        ) : (
          <>
            <span
              className={cn('truncate', !selected && 'text-muted-foreground')}
              title={selected?.label}
            >
              {selected?.label ?? placeholder}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </>
        )}
      </button>

      {open &&
        pos &&
        createPortal(
          <>
            {/* click-outside catcher. `pointer-events-auto` matters here: a
                Radix modal Dialog (or Popover) sets `document.body.style.
                pointerEvents = 'none'` and re-enables it only on its OWN
                content node — this panel is a separate document.body portal,
                so without the override it silently inherits `none` and the
                whole dropdown (search input, options) becomes unclickable
                while nested inside one. */}
            <div className="fixed inset-0 z-[9998] pointer-events-auto" onClick={() => setOpen(false)} />
            <div
              className="fixed z-[9999] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md pointer-events-auto"
              style={{ top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width }}
            >
              <div className="flex items-center gap-2 border-b px-3">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value)
                    onSearchChange?.(e.target.value)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setOpen(false)
                    if (e.key === 'Enter' && shown.length > 0) pick(shown[0].value)
                  }}
                  placeholder="Search…"
                  className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
              </div>
              <div className="max-h-64 overflow-y-auto py-1">
                {shown.length === 0 ? (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                    {options.length === 0 ? emptyMessage : 'No matches.'}
                  </div>
                ) : (
                  shown.map((o) => (
                    <button
                      type="button"
                      key={o.value}
                      title={o.label}
                      onClick={() => pick(o.value)}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted',
                        o.value === value && 'bg-muted/60',
                      )}
                    >
                      <Check
                        className={cn(
                          'h-4 w-4 shrink-0',
                          o.value === value ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      {renderOption ? renderOption(o) : <span className="truncate">{o.label}</span>}
                    </button>
                  ))
                )}
                {hiddenCount > 0 && (
                  <div className="border-t px-3 py-2 text-xs text-muted-foreground">
                    +{hiddenCount} more — refine your search
                  </div>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  )
}
